import type { BaseAPIResponse } from '~/types/api';
import type {
    BulkImportResult,
    CreatedEmployee,
    Employee,
    EmployeeAddress,
    EmployeeAka,
    EmployeeCreateInput,
    EmployeeEmail,
    EmployeeFamilyMember,
    EmployeePhoneNumber,
    PendingInvite,
} from '~/types/business';
import { NO_LICENSE_SLUG } from '~/types/licenses/tiers';
import { pbRequest, pbRequestRaw, pbRequestVoid } from '~/services/request';
import { ApiError } from '~/utils/errors';

// Employee roster: CRUD, bulk upload, invites, contact records, family
// members, and per-employee actions (scan, monthly status email).
export const employeesService = {
    /**
     * Get employees list
     */
    async getEmployees(
        companyId: number,
        params?: {
            page?: number;
            per_page?: number;
            search?: string;
            department_id?: number | null;
            risk_level?: 'high' | 'medium' | 'low' | null;
            role?: string | null;
            login_status?: 'never' | 'active' | null;
            lpoa_status?: 'signed' | 'unsigned' | null;
            sort_field?: string;
            sort_order?: 'asc' | 'desc';
        }
    ): Promise<{ data: Employee[]; meta: any }> {
        const query = new URLSearchParams();
        // Always skip expensive stats calculation for list view
        query.set('stats', 'false');
        if (params?.page) query.set('page', String(params.page));
        if (params?.per_page) query.set('per_page', String(params.per_page));
        if (params?.search) query.set('search', params.search);
        if (params?.department_id) query.set('department_id', String(params.department_id));
        if (params?.risk_level) query.set('risk_level', params.risk_level);
        if (params?.role) query.set('role', params.role);
        if (params?.login_status) query.set('login_status', params.login_status);
        if (params?.lpoa_status) query.set('lpoa_status', params.lpoa_status);
        if (params?.sort_field) query.set('sort_field', params.sort_field);
        if (params?.sort_order) query.set('sort_order', params.sort_order);

        const envelope = await pbRequestRaw<{ data: Employee[]; meta: any }>(
            `/companies/${companyId}/employees?${query.toString()}`,
            {
                failure: 'Failed to fetch employees',
            }
        );

        // New v3 index endpoint returns { data: [...], meta: {...} } directly
        return envelope as { data: Employee[]; meta: any };
    },

    /**
     * Get single employee by ID
     */
    async getEmployee(companyId: number, employeeId: number): Promise<Employee> {
        return await pbRequest<Employee>(`/companies/${companyId}/employees/${employeeId}`, {
            failure: 'Failed to fetch employee',
        });
    },

    /**
     * Create a new employee
     */
    async createEmployee(companyId: number, employeeData: EmployeeCreateInput): Promise<CreatedEmployee> {
        // V3 create endpoint expects:
        //   - emails:        [{ primary, email }]
        //   - phone_numbers: [{ primary, country_code, phone_number }]
        //   - addresses:     [{ primary, line_1, line_2, city, region, postal_code, country }]
        // Build them only when fully populated; partial input is dropped
        // to avoid backend 422s from the required-with-validation rules.
        const { email, address, phone_number, ...rest } = employeeData;
        const payload: Record<string, unknown> = {
            ...rest,
            emails: [{ primary: true, email }],
        };

        if (phone_number?.country_code && phone_number.phone_number) {
            payload.phone_numbers = [{
                primary: true,
                country_code: phone_number.country_code,
                phone_number: phone_number.phone_number,
            }];
        }

        if (address?.city && address?.country) {
            payload.addresses = [{
                primary: true,
                line_1: address.line_1 ?? '',
                line_2: address.line_2 ?? '',
                city: address.city,
                region: address.region ?? '',
                postal_code: address.postal_code ?? '',
                country: address.country,
            }];
        }

        // The outcome of the follow-up steps (seat, welcome email) rides beside
        // `data` on the envelope, so the envelope is read whole (D8).
        const envelope = await pbRequestRaw<BaseAPIResponse<Employee> & Partial<Record<'license_assigned' | 'invite_sent', boolean | null>> & { license_reason?: string | null }>(
            `/companies/${companyId}/employees`,
            {
                method: 'POST',
                body: payload,
                expect: [200, 201],
                failure: 'Failed to create employee',
            },
        );
        if (!envelope.data) throw new ApiError('Failed to create employee', 404);

        return {
            ...envelope.data,
            creation: {
                license_assigned: envelope.license_assigned ?? null,
                license_reason: envelope.license_reason ?? null,
                invite_sent: envelope.invite_sent ?? null,
            },
        };
    },

    /**
     * Update employee
     */
    async updateEmployee(companyId: number, employeeId: number, employeeData: Partial<Employee>): Promise<Employee> {
        return await pbRequest<Employee>(`/companies/${companyId}/employees/${employeeId}`, {
            method: 'PUT',
            body: employeeData,
            failure: 'Failed to update employee',
        });
    },

    /**
     * Delete employee
     */
    async deleteEmployee(companyId: number, employeeId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/employees/${employeeId}`, {
            method: 'DELETE',
            expect: [200, 204],
            failure: 'Failed to delete employee',
        });
    },

    /**
     * Download bulk upload CSV template
     */
    async downloadBulkUploadTemplate(companyId: number): Promise<void> {
        const blob = await pbRequestRaw<Blob>(`/companies/${companyId}/employees/bulk-upload/template`, {
            responseType: 'blob',
            failure: 'Failed to download the bulk upload template',
        });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'employee_bulk_upload_template.csv';
        link.click();
        URL.revokeObjectURL(link.href);
    },

    /**
     * Validate one batch of bulk upload rows (with optional column mapping).
     *
     * `rowOffset` is how many rows of the file the caller already sent: the API
     * labels every row it reports from it, so a batch's verdicts carry the row
     * number in the member's spreadsheet rather than a position inside the batch.
     */
    async validateBulkUploadRows(
        companyId: number,
        rows: Record<string, unknown>[],
        columnMapping?: Record<string, string>,
        rowOffset = 0
    ): Promise<{
        rows: Array<Record<string, unknown> & { _valid: boolean; _error?: string }>;
        valid_count: number;
        error_count: number;
    }> {
        const result = await pbRequestRaw<{ rows: unknown[]; valid_count: number; error_count: number }>(
            `/companies/${companyId}/employees/bulk-upload/validate`,
            {
                method: 'POST',
                body: { rows,
                column_mapping: columnMapping,
                row_offset: rowOffset },
                failure: 'Validation failed',
            }
        );

        return {
            rows: result.rows as Array<Record<string, unknown> & { _valid: boolean; _error?: string }>,
            valid_count: result.valid_count,
            error_count: result.error_count,
        };
    },

    /**
     * Import one batch of bulk upload rows (mapped employee objects). See
     * validateBulkUploadRows for what `rowOffset` buys.
     */
    async importBulkUploadRows(
        companyId: number,
        rows: Record<string, unknown>[],
        rowOffset = 0
    ): Promise<BulkImportResult> {
        return await pbRequestRaw<BulkImportResult>(`/companies/${companyId}/employees/bulk-upload/import`, {
            method: 'POST',
            body: { rows, row_offset: rowOffset },
            failure: 'Import failed',
        });
    },

    /**
     * Update employee license. Pass `null`, `undefined`, or an empty string
     * to remove the current license (the backend detaches person_id from the
     * existing PersonLicense; no row is deleted, no SKU lookup happens).
     * Anything else is treated as a license slug — the backend rejects
     * unknown slugs.
     */
    async updateEmployeeLicense(
        companyId: number,
        employeeId: number,
        license: string | null | undefined,
    ): Promise<void> {
        // Every wording of "no license" goes out as `NO_LICENSE_SLUG`, the one
        // value this app sends for no seat, so the backend's release branch
        // fires whichever the caller used. Left as undefined, the key would drop
        // out of the body and the backend would leave the seat where it is.
        const body = { license: license || NO_LICENSE_SLUG };
        await pbRequestVoid(`/companies/${companyId}/employees/${employeeId}`, {
            method: 'PUT',
            body,
            expect: [200, 204],
            failure: 'Failed to update employee license',
        });
    },

    /**
     * Update employee department
     * Note: Department updates use the main employee update endpoint with department_id
     */
    async updateEmployeeDepartment(companyId: number, employeeId: number, departmentId: number | null): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/employees/${employeeId}`, {
            method: 'PUT',
            body: { department_id: departmentId },
            expect: [200, 204],
            failure: 'Failed to update employee department',
        });
    },

    /**
     * Update employee roles
     */
    async updateEmployeeRoles(companyId: number, employeeId: number, roles: string[]): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/employees/${employeeId}`, {
            method: 'PUT',
            body: { roles },
            expect: [200, 204],
            failure: 'Failed to update employee roles',
        });
    },

    /**
     * Send employee invite
     */
    async sendEmployeeInvite(companyId: number, employeeId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/employees/${employeeId}/invite`, {
            method: 'POST',
            expect: [200, 204],
            failure: 'Failed to send invite',
        });
    },

    /**
     * Get pending employee invites
     */
    async getPendingInvites(
        companyId: number,
        page: number = 1,
        perPage: number = 10
    ): Promise<{
        data: PendingInvite[];
        meta: {
            current_page: number;
            last_page: number;
            per_page: number;
            total: number;
        };
    }> {
        const envelope = await pbRequestRaw<BaseAPIResponse<PendingInvite[]>>(
            `/companies/${companyId}/employees/invites?page=${page}&per_page=${perPage}`,
            {
                failure: 'Failed to fetch pending invites',
            }
        );

        // Preserve pagination meta — the invites page reads meta.total.
        return {
            data: envelope.data,
            meta: {
                current_page: envelope.meta?.current_page ?? page,
                last_page: envelope.meta?.last_page ?? page,
                per_page: envelope.meta?.per_page ?? perPage,
                total: envelope.meta?.total ?? 0,
            },
        };
    },

    /**
     * Add employee email
     */
    async addEmployeeEmail(companyId: number, employeeId: number, email: string): Promise<EmployeeEmail> {
        return await pbRequest<EmployeeEmail>(`/companies/${companyId}/employees/${employeeId}/emails`, {
            method: 'POST',
            body: { email },
            expect: [200, 201],
            failure: 'Failed to add email',
        });
    },

    /**
     * Remove employee email
     */
    async removeEmployeeEmail(companyId: number, employeeId: number, emailId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/employees/${employeeId}/emails/${emailId}`, {
            method: 'DELETE',
            expect: [200, 204],
            failure: 'Failed to remove email',
        });
    },

    /**
     * Add employee AKA (alternate name)
     */
    async addEmployeeAka(companyId: number, employeeId: number, aka: Omit<EmployeeAka, 'id'>): Promise<EmployeeAka> {
        return await pbRequest<EmployeeAka>(`/companies/${companyId}/employees/${employeeId}/akas`, {
            method: 'POST',
            body: aka,
            expect: [200, 201],
            failure: 'Failed to add alternate name',
        });
    },

    /**
     * Update an existing AKA — first/middle/last name only.
     */
    async updateEmployeeAka(
        companyId: number,
        employeeId: number,
        akaId: number,
        aka: Partial<Pick<EmployeeAka, 'first_name' | 'middle_name' | 'last_name'>>,
    ): Promise<EmployeeAka> {
        return await pbRequest<EmployeeAka>(`/companies/${companyId}/employees/${employeeId}/akas/${akaId}`, {
            method: 'PATCH',
            body: aka,
            expect: [200, 201],
            failure: 'Failed to update alternate name',
        });
    },

    /**
     * Mark an existing email primary. Backend's PersonEmail saving hook
     * flips the other rows to primary=false automatically.
     */
    async setPrimaryEmployeeEmail(companyId: number, employeeId: number, emailId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/employees/${employeeId}/emails/${emailId}`, {
            method: 'PATCH',
            body: { primary: true },
            expect: [200, 204],
            failure: 'Failed to set primary email',
        });
    },

    async setPrimaryEmployeeAddress(companyId: number, employeeId: number, addressId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/employees/${employeeId}/addresses/${addressId}`, {
            method: 'PATCH',
            body: { primary: true },
            expect: [200, 204],
            failure: 'Failed to set primary address',
        });
    },

    async setPrimaryEmployeePhoneNumber(companyId: number, employeeId: number, phoneId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/employees/${employeeId}/phone-numbers/${phoneId}`, {
            method: 'PATCH',
            body: { primary: true },
            expect: [200, 204],
            failure: 'Failed to set primary phone',
        });
    },

    /**
     * Remove employee AKA
     */
    async removeEmployeeAka(companyId: number, employeeId: number, akaId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/employees/${employeeId}/akas/${akaId}`, {
            method: 'DELETE',
            expect: [200, 204],
            failure: 'Failed to remove alternate name',
        });
    },

    /**
     * Add or update employee address
     */
    async saveEmployeeAddress(
        companyId: number,
        employeeId: number,
        address: Partial<EmployeeAddress>
    ): Promise<EmployeeAddress> {
        const method = address.id ? 'PATCH' : 'POST';
        const url = address.id
            ? `/companies/${companyId}/employees/${employeeId}/addresses/${address.id}`
            : `/companies/${companyId}/employees/${employeeId}/addresses`;

        return await pbRequest<EmployeeAddress>(url, {
            method,
            body: address,
            expect: [200, 201],
            failure: 'Failed to save address',
        });
    },

    /**
     * Remove employee address
     */
    async removeEmployeeAddress(companyId: number, employeeId: number, addressId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/employees/${employeeId}/addresses/${addressId}`, {
            method: 'DELETE',
            expect: [200, 204],
            failure: 'Failed to remove address',
        });
    },

    /**
     * Add or update employee phone number
     */
    async saveEmployeePhoneNumber(
        companyId: number,
        employeeId: number,
        phoneNumber: Partial<EmployeePhoneNumber>
    ): Promise<EmployeePhoneNumber> {
        const method = phoneNumber.id ? 'PATCH' : 'POST';
        const url = phoneNumber.id
            ? `/companies/${companyId}/employees/${employeeId}/phone-numbers/${phoneNumber.id}`
            : `/companies/${companyId}/employees/${employeeId}/phone-numbers`;

        return await pbRequest<EmployeePhoneNumber>(url, {
            method,
            body: phoneNumber,
            expect: [200, 201],
            failure: 'Failed to save phone number',
        });
    },

    /**
     * Remove employee phone number
     */
    async removeEmployeePhoneNumber(companyId: number, employeeId: number, phoneNumberId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/employees/${employeeId}/phone-numbers/${phoneNumberId}`, {
            method: 'DELETE',
            expect: [200, 204],
            failure: 'Failed to remove phone number',
        });
    },

    /**
     * Resend monthly status email to current user
     */
    async resendMonthlyStatusToMe(companyId: number, employeeId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/employees/${employeeId}/monthly-status/me`, {
            method: 'GET',
            failure: 'Failed to send email',
        });
    },

    /**
     * Resend monthly status email to employee
     */
    async resendMonthlyStatusToEmployee(companyId: number, employeeId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/employees/${employeeId}/monthly-status/employee`, {
            method: 'GET',
            failure: 'Failed to send email',
        });
    },

    /**
     * Trigger scan for employee
     */
    async triggerEmployeeScan(companyId: number, employeeId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/employees/${employeeId}/scan`, {
            method: 'POST',
            expect: [200, 204],
            failure: 'Failed to trigger scan',
        });
    },

    /**
     * List the employee's family members. Requires the company-level
     * `can_view_employee_family` flag — server returns 403 if disabled.
     */
    async getEmployeeFamily(companyId: number, employeeId: number): Promise<EmployeeFamilyMember[]> {
        const envelope = await pbRequestRaw<{ data: EmployeeFamilyMember[] }>(
            `/companies/${companyId}/employees/${employeeId}/family`,
            {
                failure: 'Failed to fetch family members',
            }
        );

        return envelope.data ?? [];
    },

    /**
     * Create an adult family member for an employee. Distinct from
     * createEmployeeMinorChild: requires a real email, has no parental-
     * consent gate, and the backend rejects minors via Person::creating's
     * age hook (use createEmployeeMinorChild for under-18 records).
     */
    async createEmployeeAdultFamily(
        companyId: number,
        employeeId: number,
        payload: {
            email: string;
            first_name: string;
            middle_name?: string | null;
            last_name: string;
            dob?: string | null;
            invite?: boolean;
            phone_number?: { country_code: string; phone_number: string };
            address?: {
                address_line_1?: string;
                address_line_2?: string;
                address_city: string;
                address_region: string;
                address_postal_code?: string;
                address_country: string;
            };
        },
    ): Promise<EmployeeFamilyMember> {
        const envelope = await pbRequestRaw<BaseAPIResponse<EmployeeFamilyMember>>(
            `/companies/${companyId}/employees/${employeeId}/family`,
            {
                method: 'POST',
                body: payload,
                expect: [200, 201],
                failure: 'Failed to add family member',
            }
        );

        return (envelope?.data ?? envelope) as EmployeeFamilyMember;
    },

    /**
     * The company lets go of one of its employee's family members (PB-007):
     * every license this company holds on them goes back to its pool at once,
     * the employee's family link is cut, and the person — their account, their
     * history, their vault — is left exactly as it stands. Nothing is deleted.
     *
     * 204 on the first call. A REPEAT answers 404, because the link it names is
     * gone and only the employee's own half of the contact pair may prove a
     * relative is theirs — accepting the relative's half would let a company act
     * on anyone who had named one of its employees. Callers read that 404 as
     * already done rather than as a failure. A refusal carries a machine-readable
     * `reason` on the `ApiError`: `relative_is_employee` when the person is an
     * employee of this company in their own right, who is offboarded instead.
     */
    async removeEmployeeFamilyMember(companyId: number, employeeId: number, relativeId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/employees/${employeeId}/family/${relativeId}`, {
            method: 'DELETE',
            expect: [200, 204],
            failure: 'Failed to remove family member',
        });
    },

    /**
     * Put a family member in one of the licenses the company ALREADY owns
     * (PB-012), through the same pool and the same refusals an employee's
     * assignment uses.
     *
     * Nothing is bought here (PB-013). An empty pool is refused 409 with
     * `reason: 'no_license_available'`, which is the caller's signal to send
     * the admin to the ordinary business checkout and assign afterwards.
     *
     * Resolves to the license the server actually holds on them, which is the
     * only thing a row may show — never the slug that was asked for.
     */
    async assignEmployeeFamilyMemberLicense(
        companyId: number,
        employeeId: number,
        relativeId: number,
        license: string,
    ): Promise<{ id: number; license: string | null }> {
        return await pbRequest<{ id: number; license: string | null }>(
            `/companies/${companyId}/employees/${employeeId}/family/${relativeId}/license`,
            {
                method: 'PUT',
                body: { license },
                failure: 'Failed to assign the license',
            }
        );
    },

    /**
     * Check whether the employee/guardian has signed the parental consent
     * agreement required before an admin can add a minor child for them.
     * Mirrors the gating used by the app-ui "Add Child" modal.
     */
    async getEmployeeMinorConsentStatus(companyId: number, employeeId: number): Promise<{ consent_signed: boolean }> {
        const envelope = await pbRequestRaw<BaseAPIResponse<{ consent_signed: boolean }>>(
            `/companies/${companyId}/employees/${employeeId}/family/minor/consent-status`,
            {
                failure: 'Failed to fetch minor consent status',
            }
        );

        // The endpoint answers `{ consent_signed }` at the top level (no `data` wrapper).
        const flag = (envelope as any)?.consent_signed ?? envelope?.data?.consent_signed;
        return { consent_signed: !!flag };
    },

    /**
     * Trigger a parental-consent email to the employee. They click the link
     * to read + sign the agreement; once signed, the admin is allowed to
     * proceed with Add Child for that employee.
     */
    async requestEmployeeMinorConsent(companyId: number, employeeId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/employees/${employeeId}/family/minor/consent-request`, {
            method: 'POST',
            expect: [200, 201, 204],
            failure: 'Failed to send consent request',
        });
    },

    /**
     * Create a minor child as a family member of the employee. The backend
     * requires the guardian's `minor_consent_signed` flag to be true; if it
     * isn't, this throws an ApiError with status 409.
     */
    async createEmployeeMinorChild(
        companyId: number,
        employeeId: number,
        payload: {
            first_name: string;
            middle_name?: string | null;
            last_name: string;
            dob: string;
            phone_number?: { country_code: string; phone_number: string };
            address?: {
                address_line_1?: string;
                address_line_2?: string;
                address_city: string;
                address_region: string;
                address_postal_code?: string;
                address_country: string;
            };
        },
    ): Promise<EmployeeFamilyMember> {
        const envelope = await pbRequestRaw<BaseAPIResponse<EmployeeFamilyMember>>(
            `/companies/${companyId}/employees/${employeeId}/family/minor`,
            {
                method: 'POST',
                body: payload,
                expect: [200, 201],
                failure: 'Failed to add child',
            }
        );

        return (envelope?.data ?? envelope) as EmployeeFamilyMember;
    },
};
